import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { translations, type Copy, type Locale } from './copy';

const STORAGE_KEY = 'reciboofertas-language';

export function resolveLocale(search: string, saved: string | null, browserLanguage: string): Locale {
  const query = new URLSearchParams(search).get('lang');
  if (query === 'es' || query === 'en') return query;
  if (saved === 'es' || saved === 'en') return saved;
  return browserLanguage.toLowerCase().startsWith('en') ? 'en' : 'es';
}

export function updateMetadata(locale: Locale, copy: Copy) {
  document.documentElement.lang = locale === 'es' ? 'es-MX' : 'en';
  document.title = copy.meta.title;
  const set = (selector: string, value: string) => document.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', value);
  set('meta[name="description"]', copy.meta.description);
  set('meta[property="og:title"]', copy.meta.title);
  set('meta[property="og:description"]', copy.meta.description);
  set('meta[property="og:locale"]', copy.meta.ogLocale);
}

type I18nValue = {
  locale: Locale;
  copy: Copy;
  setLocale: (locale: Locale) => void;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === 'undefined') return 'es';
    return resolveLocale(window.location.search, window.localStorage.getItem(STORAGE_KEY), window.navigator.language);
  });

  const copy = translations[locale];

  useEffect(() => {
    updateMetadata(locale, copy);
  }, [copy, locale]);

  const setLocale = (next: Locale) => {
    if (next === locale) return;
    const scrollY = window.scrollY;
    const url = new URL(window.location.href);
    url.searchParams.set('lang', next);
    window.history.replaceState(window.history.state, '', url);
    window.localStorage.setItem(STORAGE_KEY, next);
    setLocaleState(next);
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
      window.dispatchEvent(new Event('resize'));
    });
  };

  const value = useMemo(() => ({ locale, copy, setLocale }), [copy, locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside I18nProvider');
  return value;
}
